'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhotoGrid } from '@/components/photo-grid';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Terminal, Users, User, Upload, Gem, Plus } from 'lucide-react';
import { usePhotos, PhotoTab } from '@/hooks/use-photos';
import { useAuth } from '@/hooks/use-auth';
import Header from '@/components/header';
import { Button } from '@/components/ui/button';
import { PhotoUploadDialog } from '@/components/photo-upload-dialog';

function PhotoTabContent({ activeTab, eventId }: { activeTab: PhotoTab, eventId: string }) {
  const { photos, isLoading, error } = usePhotos(activeTab, eventId);
  const { user, loading: authLoading } = useAuth();

  if (error) {
    return (
      <Alert variant="destructive">
        <Terminal className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  if ((activeTab === 'my-photos' || activeTab === 'my-uploads') && !user && !authLoading) {
    return (
      <div className="text-center py-16">
        <p className="text-lg text-muted-foreground">
          Please log in to see your photos.
        </p>
      </div>
    );
  }

  return (
    <PhotoGrid
      photos={photos}
      loading={isLoading || authLoading}
      isMyPhotosTab={activeTab === 'my-photos' || activeTab === 'my-uploads'}
    />
  );
}

export default function PhotosPage() {
  const [activeTab, setActiveTab] = useState<PhotoTab>('public');
  const [isUploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const { user } = useAuth();
  const eventId = process.env.NEXT_PUBLIC_EVENT_ID || 'test-event';

  const handleUploadClick = () => {
    if (!user) {
      setShowLoginAlert(true);
    } else {
      setUploadDialogOpen(true);
    }
  };

  return (
    <>
      <Header />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8 text-center md:text-left">Photos</h1>
        
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as PhotoTab)} className="w-full">
          
          {/* Mobile: Bottom Nav Bar */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] md:hidden">
            <div className="pb-safe-or-4">
              <TabsList className="grid w-full grid-cols-5 items-center h-16 bg-transparent">
                
                <TabsTrigger value="public" className="flex-col bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none border-0 gap-1">
                  <Users className="h-5 w-5" fill={activeTab === 'public' ? 'currentColor' : 'none'} />
                  <span className="text-[10px] font-medium">Public</span>
                </TabsTrigger>

                <TabsTrigger value="bride-groom" className="flex-col bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none border-0 gap-1">
                  <Gem className="h-5 w-5" fill={activeTab === 'bride-groom' ? 'currentColor' : 'none'} />
                  <span className="text-[10px] font-medium">Bride/Groom</span>
                </TabsTrigger>

                <div className="flex justify-center items-center relative -top-8">
                  <div className="bg-white rounded-full p-2">
                    <Button size="icon" className="rounded-full h-14 w-14 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20" onClick={handleUploadClick}>
                      <Plus className="h-8 w-8 text-white" />
                    </Button>
                  </div>
                </div>

                <TabsTrigger value="my-photos" className="flex-col bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none border-0 gap-1">
                  <User className="h-5 w-5" fill={activeTab === 'my-photos' ? 'currentColor' : 'none'} />
                  <span className="text-[10px] font-medium">My Photos</span>
                </TabsTrigger>

                <TabsTrigger value="my-uploads" className="flex-col bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none border-0 gap-1">
                  <Upload className="h-5 w-5" fill={activeTab === 'my-uploads' ? 'currentColor' : 'none'} />
                  <span className="text-[10px] font-medium">Uploads</span>
                </TabsTrigger>

              </TabsList>
            </div>
          </div>

          <div className="md:grid md:grid-cols-4 lg:grid-cols-5 gap-8">
            {/* Desktop: Sidebar */}
            <div className="hidden md:block md:col-span-1 lg:col-span-1">
              <TabsList className="flex flex-col items-start h-auto bg-transparent space-y-2">
                  <TabsTrigger value="public" className="w-full justify-start gap-2 px-3 py-2 data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground rounded-md">
                      <Users className="h-5 w-5" fill={activeTab === 'public' ? 'currentColor' : 'none'} />
                      <span className="font-medium">Public</span>
                  </TabsTrigger>
                  <TabsTrigger value="bride-groom" className="w-full justify-start gap-2 px-3 py-2 data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground rounded-md">
                      <Gem className="h-5 w-5" fill={activeTab === 'bride-groom' ? 'currentColor' : 'none'} />
                      <span className="font-medium">Bride/Groom</span>
                  </TabsTrigger>
                  <TabsTrigger value="my-photos" className="w-full justify-start gap-2 px-3 py-2 data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground rounded-md">
                      <User className="h-5 w-5" fill={activeTab === 'my-photos' ? 'currentColor' : 'none'} />
                      <span className="font-medium">My Photos</span>
                  </TabsTrigger>
                  <TabsTrigger value="my-uploads" className="w-full justify-start gap-2 px-3 py-2 data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground rounded-md">
                      <Upload className="h-5 w-5" fill={activeTab === 'my-uploads' ? 'currentColor' : 'none'} />
                      <span className="font-medium">My Uploads</span>
                  </TabsTrigger>
              </TabsList>
              <Button className="w-full mt-8" onClick={handleUploadClick}>
                <Plus className="-ml-2 mr-2 h-5 w-5"/>
                Upload Photo
              </Button>
            </div>

            {/* Main Content */}
            <div className="md:col-span-3 lg:col-span-4 mt-4 md:mt-0 pb-24 md:pb-0">
              <TabsContent value="public"><PhotoTabContent activeTab="public" eventId={eventId} /></TabsContent>
              <TabsContent value="bride-groom"><PhotoTabContent activeTab="bride-groom" eventId={eventId} /></TabsContent>
              <TabsContent value="my-photos"><PhotoTabContent activeTab="my-photos" eventId={eventId} /></TabsContent>
              <TabsContent value="my-uploads"><PhotoTabContent activeTab="my-uploads" eventId={eventId} /></TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
      <PhotoUploadDialog 
        isOpen={isUploadDialogOpen}
        setIsOpen={setUploadDialogOpen}
        eventId={eventId}
      />
      <AlertDialog open={showLoginAlert} onOpenChange={setShowLoginAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Please Log In</AlertDialogTitle>
            <AlertDialogDescription>
              You need to be logged in to upload photos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Link href="/login">Log In</Link>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
